// A1-3 / A1-5 — THE TRIAGE QUEUE, HER SEAT.
//
// **DRAFTS LAND HERE, NOT IN RUNBOOK.md.** Nothing in this view is an order until she taps approve;
// that is her ruling and it is the whole of phase 1.
//
// The view is organised by the thing that actually matters: WHOSE PILE IS THIS. The technical lane
// is the only one that carries a draft order, and the other three are simply her messages, shown
// with the reason they were routed to her and a door into the member. There is deliberately no
// "approve" control on a financial or personal row — not disabled, ABSENT — because the safest
// version of "financial never auto-anything" is a screen where the control does not exist.
import { useCallback, useEffect, useState } from "react";
import { loadOwnerData, ownerLoadNote, postOwnerAction, postOwnerConfirmed, ownerActionNote } from "../lib/owner-load";
import { BellRing, Check, Pencil, Stethoscope, Wrench, X } from "lucide-react";
import { CATEGORIES, PHASES, mayAgentAct } from "../triage.js";
// TS1(2) — the verification's own words come from the law, never from here. A verdict label typed at
// this call site is a second answer key for what `confirmed` means (R28/R29), and `cannot-tell` is
// the one that must never quietly render as a clean bill.
import { verificationHeadline, verdictFor, fixPlan } from "../ticketVerification.js";

type Category = { key: string; label: string; note: string; auto: boolean };
type Draft = {
  id: string; contactId: string; userId: string | null; memberName: string; email: string;
  category: string; classifiedBy: string; why: string; draft: string | null; title: string;
  message: string; subject: string; status: string; decidedAt: string | null; decidedBy: string | null;
  rejectNote: string | null; correctedTo: string | null;
  acknowledged: boolean; closed: boolean; createdAt: string;
};
type Cause = { key: string; label: string; why: string; act: string };
type VCheck = { class: string; label: string; verdict: string; cause: Cause | null; detail: string };
type Verification = {
  verdict: string; focus: string[]; checks: VCheck[]; causes: Cause[]; unmeasured: string[];
  facts: { readings?: number; newest?: { type: string; at: string } | null } | null;
};
type Payload = {
  drafts: Draft[];
  stats: { pending: number; approved: number; rejected: number; hers: number; closures: number };
  phase: number;
  lessons: Array<{ was: string; shouldBe: string | null; note: string | null; at: string }>;
};

const stamp = (iso: string) => (iso ? new Date(iso).toLocaleString() : "");
const cats = CATEGORIES as Category[];
const catLabel = (k: string) => cats.find((c) => c.key === k)?.label || k;

export function OwnerTriage({ onDenied, onOpenMember, openQuery = "" }: {
  onDenied: () => void; onOpenMember?: (id: string) => void;
  // >>> M1 — **THE DOOR'S OWN SELECTION, AND IT IS AN OPENING RATHER THAN A LOCK.** <<< Her Overview
  // line counts `triage_drafts WHERE category='financial' AND status='pending'` and opened `grants`,
  // a different table entirely. It opens here now, and this prop is what makes it land on the seven
  // rows the number counted rather than on all 26 pending drafts.
  //
  // **IT SEEDS THE FILTERS, IT DOES NOT REPLACE THEM.** Her own category and status controls stay
  // live and she can widen out of it in one tap — a door that pinned the view would be a second
  // defect wearing the fix, and her clause is *decidable right there*, not *decidable only there*.
  openQuery?: string;
}) {
  const [data, setData] = useState<Payload | null>(null);
  const [loadNote, setLoadNote] = useState("");
  // R66: what the SERVER said it did, or why it did not.
  const [actionNote, setActionNote] = useState<{ text: string; ok: boolean }>({ text: "", ok: true });
  // M1 — the door's selection seeds her own two controls.
  //
  // >>> **IT IS RE-SEEDED WHEN THE DOOR CHANGES, NOT ONLY AT MOUNT, AND THAT IS A LESSON THIS HOUSE
  // HAS ALREADY PAID FOR.** <<< `useState(initial)` reads its argument on the FIRST render only, and
  // ITEM A's walk measured exactly that defect one view over: two doors rendering the same component
  // at the same position are reconciled as ONE instance that never remounts, so the second door's
  // parameter was live, correct, and consumed by nobody. React's own adjust-state-during-render
  // pattern is the repair — state rather than a ref, so it is correct under StrictMode — and it
  // fires only when the door really moves, so **her own taps on these controls are untouched.**
  const [seededFrom, setSeededFrom] = useState<string | null>(null);
  const [status, setStatus] = useState("pending");
  const [category, setCategory] = useState("");
  if (seededFrom !== openQuery) {
    const opened = new URLSearchParams(String(openQuery || ""));
    setSeededFrom(openQuery);
    setStatus(opened.get("status") || "pending");
    setCategory(opened.get("category") || "");
  }
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [rejecting, setRejecting] = useState<Draft | null>(null);
  const [note, setNote] = useState("");
  // TS1(2) — one verification per ticket, kept by ticket id. **THREE STATES AND NOT TWO** (R64/R84,
  // one panel over): absent is "not asked", `null` is "asked and the read did not land", and only a
  // real result is a verdict — so a failed read can never render as a member with nothing wrong.
  const [verify, setVerify] = useState<Record<string, Verification | null>>({});
  const [verifying, setVerifying] = useState("");

  // TS1(5) — the closure note, D2's double post. **STEP TWO IS DERIVED FROM STEP ONE'S PAYLOAD**
  // rather than typed beside it (`postOwnerConfirmed`, R31), so the confirm cannot re-post something
  // other than what she was shown.
  const [closing, setClosing] = useState<{ id: string; sentence: string } | null>(null);
  const sendClosureNote = async (id: string, confirmed: boolean) => {
    const body = { id };
    const answer = confirmed
      ? await postOwnerConfirmed<{ sentence?: string }>("/api/owner/triage/closure", body)
      : await postOwnerAction<{ sentence?: string }>("/api/owner/triage/closure", body);
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    if (!answer.ok && answer.kind === "confirm") { setClosing({ id, sentence: answer.sentence }); return; }
    setClosing(null);
    // R66: what the SERVER said it DID, which on this act is the difference between a note that went
    // and one the two-touch law refused.
    setActionNote({ text: ownerActionNote(answer), ok: answer.ok });
    if (answer.ok) load();
  };

  const runVerify = useCallback((id: string) => {
    setVerifying(id);
    loadOwnerData<{ verification: Verification }>(`/api/owner/triage/verify?id=${encodeURIComponent(id)}`)
      .then((answer) => {
        if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
        // R39/R47: the ANSWER is read, never the status code, and a refusal leaves the honest `null`
        // rather than an empty panel that reads as a clean account.
        setVerify((v) => ({ ...v, [id]: answer.ok ? answer.data.verification : null }));
        if (!answer.ok) setActionNote({ text: ownerLoadNote(answer), ok: false });
      })
      .finally(() => setVerifying(""));
  }, [onDenied]);

  // TS1(3) — THE FIX LINE. D2's double post again, and step two DERIVED from step one (R31).
  //
  // **THE BODY CARRIES A TICKET ID AND NOTHING ELSE.** There is deliberately no repair key on this
  // request: the server picks the repair from the cause it verifies in that same request, so nothing
  // this screen can send decides what happens to somebody's account.
  const [repairing, setRepairing] = useState<{ id: string; sentence: string } | null>(null);
  const runRepair = async (id: string, confirmed: boolean) => {
    const answer = confirmed
      ? await postOwnerConfirmed<{ sentence?: string }>("/api/owner/triage/repair", { id })
      : await postOwnerAction<{ sentence?: string }>("/api/owner/triage/repair", { id });
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    if (!answer.ok && answer.kind === "confirm") { setRepairing({ id, sentence: answer.sentence }); return; }
    setRepairing(null);
    setActionNote({ text: ownerActionNote(answer), ok: answer.ok });
    // A repair that landed changes what her account resolves to, so the panel is re-read rather than
    // left showing the state that was true before it ran (R53: a stale panel is a wrong panel).
    if (answer.ok) runVerify(id);
  };

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams({ status, q });
    if (category) qs.set("category", category);
    loadOwnerData<Payload>(`/api/owner/triage?${qs.toString()}`)
      .then((answer) => {
        if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
        if (answer.ok) setData(answer.data);
        setLoadNote(ownerLoadNote(answer));
      })
      .finally(() => setLoading(false));
  }, [status, category, q, onDenied]);
  useEffect(() => { load(); }, [load]);

  // R66 — **NEITHER OF THESE READ ITS ANSWER AT ALL, NOT EVEN THE STATUS CODE.** `.catch(() => {})`
  // and then, unconditionally: close the editor, throw away the reason she typed, and re-read. So a
  // refused decision left the draft exactly where it was and said nothing — which does not read as a
  // failure, it reads as a STUBBORN ROW — **and her typed "why" was gone**, which is R61's bookmark
  // ruling word for word. Both are held by POSITION: the answer is read, a refusal returns, and only
  // an act that landed clears what she wrote.
  const decide = async (body: Record<string, unknown>) => {
    const answer = await postOwnerAction<{ sentence?: string }>("/api/owner/triage/decide", body);
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    setActionNote({ text: ownerActionNote(answer), ok: answer.ok });
    if (!answer.ok) return;
    setEditing(null); setRejecting(null); setNote("");
    load();
  };

  const setPhase = async (phase: number) => {
    const answer = await postOwnerAction<{ sentence?: string }>("/api/owner/triage/phase", { phase });
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    setActionNote({ text: ownerActionNote(answer), ok: answer.ok });
    if (!answer.ok) return;
    load();
  };

  return (
    <div className="owner-triage">
      {data ? (
        <div className="owner-stats">
          <div className="owner-stat"><strong>{data.stats.pending}</strong><span>Drafts waiting for you</span></div>
          <div className="owner-stat"><strong>{data.stats.hers}</strong><span>Your pile</span></div>
          <div className="owner-stat"><strong>{data.stats.approved}</strong><span>Approved</span></div>
          <div className="owner-stat"><strong>{data.stats.rejected}</strong><span>Rejected</span></div>
          <div className="owner-stat"><strong>{data.stats.closures}</strong><span>Fix-linked closures</span></div>
        </div>
      ) : null}

      {/* A1-3 — TRAINING WHEELS, AND THE SWITCH IS HERS. Phase 2 is earned in the ledger below, and
          even in phase 2 it is TECHNICAL ONLY: financial and personal never auto-anything in any
          phase, which is enforced on the server by a rule that has no phase in it. */}
      <div className="owner-triage__phase">
        <strong>Training wheels</strong>
        {[1, 2].map((n) => (
          <button key={n} type="button"
            className={data?.phase === n ? "owner-range__btn is-active" : "owner-range__btn"}
            onClick={() => setPhase(n)}>
            Phase {n}: {(PHASES as Record<number, { label: string }>)[n].label}
          </button>
        ))}
        <span className="owner-page__note">Money and personal messages always wait for you, in both phases.</span>
      </div>

      <div className="owner-bar">
        <input className="owner-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search what they wrote..." aria-label="Search messages" />
        <div className="owner-range">
          {[["pending", "Waiting"], ["approved", "Approved"], ["rejected", "Rejected"]].map(([k, label]) => (
            <button key={k} type="button" className={status === k ? "owner-range__btn is-active" : "owner-range__btn"}
              onClick={() => setStatus(k)}>{label}</button>
          ))}
        </div>
        <div className="owner-range">
          <button type="button" className={!category ? "owner-range__btn is-active" : "owner-range__btn"}
            onClick={() => setCategory("")}>All</button>
          {cats.map((c) => (
            <button key={c.key} type="button" title={c.note}
              className={category === c.key ? "owner-range__btn is-active" : "owner-range__btn"}
              onClick={() => setCategory(c.key)}>{c.label}</button>
          ))}
        </div>
      </div>

      {loading ? <p className="owner-page__note">Loading...</p> : null}
      {/* R65: same ruling one tab over. "Nothing here." over a failed read says the queue is clear. */}
      {!loading && loadNote ? <p className="owner-page__note owner-fresh--failed" role="status">{loadNote}</p> : null}
      {/* R66: what the last act DID, or why it did not go. The server composes both (the D2 law). */}
      {actionNote.text ? <p className={actionNote.ok ? "owner-page__note owner-act-note" : "owner-page__note owner-fresh--failed"} role="status">{actionNote.text}</p> : null}
      {!loading && !loadNote && !data?.drafts.length ? <p className="owner-page__note">Nothing here.</p> : null}

      <div className="owner-triage__list">
        {(data?.drafts || []).map((d) => (
          <article key={d.id} className={`owner-triage__item is-${d.category}`}>
            <header>
              <span className={`owner-triage__cat is-${d.category}`}>{catLabel(d.category)}</span>
              {/* A1-4: the member is LINKED so the fix is verified against their experience. */}
              {d.userId && onOpenMember ? (
                <button type="button" className="owner-link" onClick={() => onOpenMember(d.userId as string)}>
                  {d.memberName || d.email}
                </button>
              ) : <span>{d.memberName || d.email}</span>}
              <span className="owner-triage__when">{stamp(d.createdAt)}</span>
              {d.acknowledged ? <em className="owner-triage__touch">acknowledged</em> : null}
              {d.closed ? <em className="owner-triage__touch">closed</em> : null}
            </header>

            {/* HER WORDS, VERBATIM, ALWAYS SHOWN. The sacred-words law: the queue never renders a
                summary in place of what the member actually wrote. */}
            <blockquote className="owner-triage__said">{d.message}</blockquote>
            <p className="owner-triage__why">Sorted by the {d.classifiedBy}: {d.why}</p>

            {/* TS1(2) — HER CLAUSE: is it REAL, and what is the cause. It reads that member's own
                state and CHANGES NOTHING, so it sits on every row rather than only the technical
                lane: the tickets in her pile are exactly the ones where knowing beforehand is worth
                most. The verdict, the labels and every cause come from the law. */}
            <div className="owner-triage__verify">
              {!(d.id in verify) ? (
                <button type="button" className="owner-link" disabled={verifying === d.id}
                  onClick={() => runVerify(d.id)}>
                  <Stethoscope size={14} />{verifying === d.id ? "Checking their account..." : "Check their account"}
                </button>
              ) : verify[d.id] === null ? (
                /* The read did not land. NOT a clean account (R18) — it says so and offers a retry. */
                <p className="owner-page__note owner-fresh--failed" role="status">
                  Their account could not be read just now, so nothing here is a verdict.{" "}
                  <button type="button" className="owner-link" onClick={() => runVerify(d.id)}>Try again</button>
                </p>
              ) : (
                <>
                  <p className={`owner-triage__verdict is-${(verify[d.id] as Verification).verdict}`}>
                    {verificationHeadline(verify[d.id])}
                    <em>{verdictFor((verify[d.id] as Verification).verdict)?.why || ""}</em>
                  </p>
                  <ul className="owner-triage__lanes">
                    {(verify[d.id] as Verification).checks.map((c) => (
                      <li key={c.class} className={`is-${c.verdict}${(verify[d.id] as Verification).focus.includes(c.class) ? " is-focus" : ""}`}>
                        <strong>{c.label}</strong>
                        <span>{c.detail}</span>
                        {/* The cause, in the roster's own words: what it is, why, and whose it is. */}
                        {c.cause ? <em>{c.cause.label} — {c.cause.why} {c.cause.act}</em> : null}
                      </li>
                    ))}
                  </ul>
                  {/* TS1(3) — THE FIX LINE. Every row here is DERIVED from a cause the pass just
                      named (R31): what may be repaired now, what becomes a trace-first order, what
                      resolves itself, and what is hers to lift. The words are the law's.
                      **THE REPAIR CONTROL IS ABSENT RATHER THAN DISABLED ON A PILE THAT MAY NOT BE
                      ACTED ON** — this file's own opening ruling, and it asks `mayAgentAct` rather
                      than testing a category here, so there is one answer to (c). */}
                  {fixPlan(verify[d.id]).rows.length ? (
                    <ul className="owner-triage__fix">
                      {fixPlan(verify[d.id]).rows.map((r: { class: string; label: string; disposition: { key: string; label: string; note: string }; repair: { label: string } | null }) => (
                        <li key={r.class} className={`is-${r.disposition.key}`}>
                          <strong>{r.disposition.label}</strong>
                          <span>{r.label}: {r.disposition.note}</span>
                          {r.repair ? <em>{r.repair.label}</em> : null}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {mayAgentAct({ category: d.category }) && fixPlan(verify[d.id]).repairs.length ? (
                    <footer>
                      <button type="button" className="owner-link" onClick={() => runRepair(d.id, false)}>
                        <Wrench size={14} />Perform the known repair
                      </button>
                    </footer>
                  ) : null}
                  {(verify[d.id] as Verification).facts ? (
                    <p className="owner-page__note">
                      {(verify[d.id] as Verification).facts?.readings ?? 0} readings on their shelf
                      {(verify[d.id] as Verification).facts?.newest
                        ? `, newest ${(verify[d.id] as Verification).facts?.newest?.type} on ${stamp(String((verify[d.id] as Verification).facts?.newest?.at || ""))}`
                        : ""}
                    </p>
                  ) : null}
                </>
              )}
            </div>

            {d.category === "technical" && d.draft ? (
              <>
                <details className="owner-triage__draft">
                  <summary>The drafted order</summary>
                  <pre>{d.draft}</pre>
                </details>
                {d.status === "pending" ? (
                  <footer>
                    <button type="button" className="owner-link" onClick={() => decide({ id: d.id, action: "approve" })}>
                      <Check size={14} />Approve
                    </button>
                    <button type="button" className="owner-link" onClick={() => setEditing(d)}>
                      <Pencil size={14} />Edit
                    </button>
                    <button type="button" className="owner-link" onClick={() => setRejecting(d)}>
                      <X size={14} />Reject
                    </button>
                  </footer>
                ) : (
                  <p className="owner-page__note">
                    {d.status} {d.decidedBy ? `by ${d.decidedBy}` : ""} {d.decidedAt ? `on ${stamp(d.decidedAt)}` : ""}
                    {d.rejectNote ? ` — "${d.rejectNote}"` : ""}
                  </p>
                )}
                {/* TS1(5) — THE SECOND TOUCH, WHICH EXISTED AND WAS WIRED TO NOTHING. It is offered
                    only on an APPROVED order (the fix became real work) that has a member behind it
                    and has not been closed — and the two-touch cap is the ROW's, not this control's:
                    `stampTouch` claims before it delivers, so a double tap sends one note. */}
                {d.status === "approved" && d.userId && !d.closed ? (
                  <footer>
                    <button type="button" className="owner-link" onClick={() => sendClosureNote(d.id, false)}>
                      <BellRing size={14} />Tell them it is fixed
                    </button>
                  </footer>
                ) : null}
              </>
            ) : (
              /* NO APPROVE CONTROL EXISTS ON THESE ROWS. Not disabled: absent. */
              <p className="owner-page__note">This one is yours. Open the member to act on it.</p>
            )}
          </article>
        ))}
      </div>

      {/* A1-3 — WHAT HER REJECTIONS TAUGHT. The same principle as LR1-15's overturn pressure: a
          category she keeps correcting is a classifier problem, not a run of bad luck. */}
      {data?.lessons?.length ? (
        <>
          <h3 className="owner-lr__logtitle">What your rejections have taught</h3>
          <ul className="owner-lr__log">
            {data.lessons.slice(0, 12).map((l, i) => (
              <li key={i}>
                <span>{stamp(l.at)}</span>
                <strong>{catLabel(l.was)}{l.shouldBe ? ` → ${catLabel(l.shouldBe)}` : ""}</strong>
                {l.note || ""}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {editing ? (
        <div className="modal-backdrop lr-modal-backdrop" role="dialog" aria-modal="true" aria-label="Edit the order">
          <div className="lr-modal lr-modal--edit">
            <h2>Edit the order, then approve it</h2>
            <textarea rows={16} defaultValue={editing.draft || ""} id="triage-edit" />
            <div className="lr-confirm">
              <button type="button" className="button button--ghost" onClick={() => setEditing(null)}>Never mind</button>
              <button type="button" className="button" onClick={() => decide({
                id: editing.id, action: "approve",
                draft: (document.getElementById("triage-edit") as HTMLTextAreaElement)?.value || editing.draft,
              })}>Approve</button>
            </div>
          </div>
        </div>
      ) : null}

      {/* TS1(5) — D2's second post, and the sentence in it is the SERVER'S, carrying the exact words
          the member will read. A confirm written here could describe something other than what the
          server then does. */}
      {closing ? (
        <div className="modal-backdrop lr-modal-backdrop" role="dialog" aria-modal="true" aria-label="Send the closure note">
          <div className="lr-modal lr-modal--confirm">
            <h2>Tell them it is fixed</h2>
            <p className="owner-page__note">{closing.sentence}</p>
            <div className="lr-confirm">
              <button type="button" className="button button--ghost" onClick={() => setClosing(null)}>Never mind</button>
              <button type="button" className="button" onClick={() => sendClosureNote(closing.id, true)}>Send it</button>
            </div>
          </div>
        </div>
      ) : null}

      {/* TS1(3) — the same double post on the one act that WRITES to a member's account. The
          sentence names the repair, the cause behind it and why it is safe, and every word of it is
          the SERVER'S. */}
      {repairing ? (
        <div className="modal-backdrop lr-modal-backdrop" role="dialog" aria-modal="true" aria-label="Perform the known repair">
          <div className="lr-modal lr-modal--confirm">
            <h2>Perform the known repair</h2>
            <p className="owner-page__note">{repairing.sentence}</p>
            <div className="lr-confirm">
              <button type="button" className="button button--ghost" onClick={() => setRepairing(null)}>Never mind</button>
              <button type="button" className="button" onClick={() => runRepair(repairing.id, true)}>Do it</button>
            </div>
          </div>
        </div>
      ) : null}

      {rejecting ? (
        <div className="modal-backdrop lr-modal-backdrop" role="dialog" aria-modal="true" aria-label="Reject">
          <div className="lr-modal lr-modal--confirm">
            <h2>Reject this draft</h2>
            <p className="owner-page__note">Nothing enters the runbook. What you say here teaches the sorting.</p>
            <select id="triage-corrected" defaultValue="">
              <option value="">It should not have been drafted at all</option>
              {cats.filter((c) => c.key !== "technical").map((c) => (
                <option key={c.key} value={c.key}>It was really {c.label}</option>
              ))}
            </select>
            <textarea rows={3} value={note} placeholder="Why (optional)" onChange={(e) => setNote(e.target.value)} />
            <div className="lr-confirm">
              <button type="button" className="button button--ghost" onClick={() => { setRejecting(null); setNote(""); }}>Never mind</button>
              <button type="button" className="button" onClick={() => decide({
                id: rejecting.id, action: "reject", note,
                correctedTo: (document.getElementById("triage-corrected") as HTMLSelectElement)?.value || "",
              })}>Reject</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
